import moment from 'moment';
import React, { useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Check } from 'react-bootstrap-icons';
import CircularProgressBar from '../circular-progress';
import useTaskCard from '../../hooks/useTaskCard';
import {
  formatAssigneeName,
  getTaskDisplayTitle,
  getTaskStatusBadge,
  getTaskVisualStatus,
  hasTaskDescription
} from './taskCardUtils';
import { Copy, Edit2, Trash2 } from 'lucide-react';
import { STATUS_STYLES } from '../../helpers/constants';
import { looksLikeHtml, toDisplayableRichTextHtml, toRichTextEditorValue } from '../../helpers/safeHtml';
import QualiaSentConfirmModal from './QualiaSentConfirmModal';

const ServiceCardTaskCard = ({ task, onStatusUpdate, onEditClick, onTaskDelete, fromDetails = false }) => {
  const {
    isClientUser,
    canDeleteTask,
    updatingStatus,
    toggleTaskStatus,
    bizDaysLabel,
    assignedProcessors,
    isQualiaTask,
    showQualiaConfirm,
    confirmQualiaSent,
    cancelQualiaConfirm,
    copyQualiaMessage
  } = useTaskCard({ task, onStatusUpdate, fromDetails });

  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const visualStatus = getTaskVisualStatus(task);
  const statusStyle = STATUS_STYLES[visualStatus];
  const statusBadge = getTaskStatusBadge(task);
  const showDescriptionToggle = hasTaskDescription(task);

  const renderTooltip = props => (
    <Tooltip {...props}>
      {assignedProcessors.slice(2).map(p => (
        <React.Fragment key={p._id || p.name}>
          {p.name}
          <br />
        </React.Fragment>
      ))}
    </Tooltip>
  );

  const RenderAssignedTo = () => {
    if (!assignedProcessors.length) return null;

    if (assignedProcessors.length < 3) {
      return assignedProcessors.map(a => formatAssigneeName(a.name)).join(' & ');
    }

    return (
      <>
        {assignedProcessors
          .slice(0, 2)
          .map(a => formatAssigneeName(a.name))
          .join(', ')}{' '}
        &amp;{' '}
        <OverlayTrigger placement="right" overlay={renderTooltip}>
          <u>
            {assignedProcessors.length - 2} other
            {assignedProcessors.length - 2 > 1 && 's'}
          </u>
        </OverlayTrigger>
      </>
    );
  };

  const renderDetailLine = () => {
    const assignee = <RenderAssignedTo />;
    const style = { color: statusStyle.detailColor, fontSize: 11.5, marginTop: 3 };

    if (visualStatus === 'completed') {
      return (
        <div style={style}>
          {assignee}
          {assignedProcessors.length > 0 && ' · '}
          Created {moment(task.createdAt).format('MMM D')} {' · '}
          {bizDaysLabel} · Completed {moment(task.updatedAt || task.dueDate).format('MMM D')}
        </div>
      );
    }

    if (visualStatus === 'overdue') {
      return (
        <div style={style}>
          {assignee}
          {assignedProcessors.length > 0 && ' · '}
          Created {moment(task.createdAt).format('MMM D')} {' · '} {bizDaysLabel}
        </div>
      );
    }

    return (
      <div style={style}>
        {assignee}
        {assignedProcessors.length > 0 && ' · '}
        Created {moment(task.createdAt).format('MMM D')} {' · '}
        {bizDaysLabel} · Due {moment(task.dueDate).format('MMM D')}
      </div>
    );
  };

  const circleStyle = {
    width: 17,
    height: 17,
    borderRadius: '50%',
    backgroundColor: statusStyle.filled ? statusStyle.circleBorder : '#fff',
    border: `2px solid ${statusStyle.circleBorder}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2
  };

  const renderStatusIcon = () => {
    if (isClientUser) {
      return <div style={circleStyle}>{statusStyle.filled && <Check size={11} color="#fff" />}</div>;
    }

    if (updatingStatus) {
      return (
        <div style={{ marginTop: 2, flexShrink: 0 }}>
          <CircularProgressBar size={1.2} />
        </div>
      );
    }

    return (
      <div
        role="button"
        tabIndex={0}
        className="hover"
        style={{ ...circleStyle, cursor: 'pointer' }}
        onClick={toggleTaskStatus}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleTaskStatus();
          }
        }}
      >
        {statusStyle.filled ? <Check size={11} color="#fff" /> : null}
      </div>
    );
  };

  return (
    <>
      <div
        className="mb-2"
        style={{
          backgroundColor: statusStyle.background,
          border: `1px solid ${statusStyle.border}`,
          borderRadius: 9,
          padding: '11px 13px'
        }}
      >
        <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
          {renderStatusIcon()}

          <div className="flex-grow-1 min-width-0" style={{ minWidth: 0 }}>
            <div
              style={{
                color: statusStyle.titleColor,
                fontWeight: statusStyle.titleBold ? 700 : 400,
                textDecoration: statusStyle.titleStrikethrough ? 'line-through' : 'none',
                fontSize: 13.5,
                lineHeight: 1.3,
                wordBreak: 'break-word'
              }}
            >
              {getTaskDisplayTitle(task)}
            </div>
            {renderDetailLine()}

            {showDescriptionToggle && (
              <button
                type="button"
                className="task-card-desc-toggle"
                onClick={e => {
                  e.stopPropagation();
                  setDescriptionExpanded(open => !open);
                }}
              >
                {descriptionExpanded ? '▾ Hide details' : '▸ Show details'}
              </button>
            )}
          </div>

          <span
            style={{
              color: statusBadge.textColor,
              backgroundColor: statusBadge.background,
              padding: '3px 10px',
              borderRadius: 20,
              fontSize: 10.5,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              lineHeight: 1.4
            }}
          >
            {statusBadge.label}
          </span>

          {!isClientUser && (
            <div className="d-flex align-items-center" style={{ flexShrink: 0, marginTop: -2 }}>
              {isQualiaTask && (
                <Button
                  size="sm"
                  variant="link"
                  className="d-flex align-items-center bg-transparent border-0 hover-muted px-2 py-1 shadow-none text-info"
                  style={{ borderRadius: '12px', fontSize: 11, fontWeight: 600, gap: 4 }}
                  onClick={copyQualiaMessage}
                >
                  <Copy size={14} />
                  Copy
                </Button>
              )}
              <Button
                size="sm"
                variant="link"
                className="d-flex align-items-center justify-content-center bg-transparent border-0 hover-muted p-1 shadow-none"
                style={{ borderRadius: '12px' }}
                onClick={() => onEditClick(task)}
              >
                <Edit2 size={14} color="var(--info)" />
              </Button>
              {canDeleteTask && (
                <Button
                  size="sm"
                  variant="outline-danger"
                  className="d-flex align-items-center justify-content-center bg-transparent border-0 hover-muted p-1 shadow-none"
                  style={{ borderRadius: '12px' }}
                  onClick={() => onTaskDelete(task)}
                >
                  <Trash2 size={14} color="var(--danger)" />
                </Button>
              )}
            </div>
          )}
        </div>

        {showDescriptionToggle && descriptionExpanded && (
          <div
            className="task-card-desc"
            style={{ marginLeft: 28 }}
            dangerouslySetInnerHTML={{
              __html: toDisplayableRichTextHtml(
                looksLikeHtml(task.description) ? task.description : toRichTextEditorValue(task.description)
              )
            }}
          />
        )}
      </div>

      <QualiaSentConfirmModal
        show={showQualiaConfirm}
        updating={updatingStatus}
        onConfirm={confirmQualiaSent}
        onCancel={cancelQualiaConfirm}
      />
    </>
  );
};

export default ServiceCardTaskCard;
